"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Share2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import { ShareSnippetDropdownBody } from "./ShareSnippetDropdownBody";
import { cn } from "@/lib/utils";

type ShareSnippetPopoverProps = {
  locale: string;
  snippetId: string;
  /** When already known (e.g. after load), skip POST until user opens. */
  initialShareSlug?: string | null;
  initialIsPasswordProtected?: boolean;
  disabled?: boolean;
  /** Why the trigger is off when it is not "save first" (offline, `PG-PWA01`). */
  disabledTitle?: string;
  triggerClassName?: string;
};

/**
 * Share control: generates public link via API, shows URL + copy (Radix dropdown — project has `@radix-ui/react-dropdown-menu`).
 */
export function ShareSnippetPopover({
  locale,
  snippetId,
  initialShareSlug,
  initialIsPasswordProtected = false,
  disabled = false,
  disabledTitle,
  triggerClassName,
}: ShareSnippetPopoverProps) {
  const t = useTranslations("playground");
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
  }, []);

  const hint = disabledTitle ?? t("shareDisabledHint");

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      {/*
        The trigger's `asChild` child is the `<button>` itself, not a wrapper.
        Radix merges `type`, `aria-haspopup` and `aria-expanded` onto whatever
        it is given, and putting those on the layout `<span>` made them invalid
        ARIA on a generic element (axe `aria-allowed-attr`, critical). The
        layout classes ride on the button instead, and the disabled tooltip
        moves to the button's own `title`, which is where a pointer looks for it.
      */}
      <DropdownMenu.Trigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          aria-label={disabled ? hint : t("share")}
          title={disabled ? hint : t("share")}
          className={cn(
            "inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 text-sm font-medium text-white/90 transition hover:bg-white/10",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]",
            "disabled:cursor-not-allowed disabled:opacity-40",
            triggerClassName,
          )}
        >
          <Share2 className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          <span className="hidden xl:inline">{t("share")}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] w-[min(100vw-2rem,380px)] rounded-xl border border-white/10 bg-[#141416] p-3 shadow-2xl"
          sideOffset={6}
          align="end"
          collisionPadding={8}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <ShareSnippetDropdownBody
            locale={locale}
            snippetId={snippetId}
            initialShareSlug={initialShareSlug}
            initialIsPasswordProtected={initialIsPasswordProtected}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
